/**
 * Live camera video for the dashboards.
 *
 * Hub mode always pulls from the Cloudflare SFU with a token from the hub.
 * Local mode pulls from the SFU with a token from the robot, or reads the
 * robot's own MJPEG feed. The local source comes from `LocalCameraSourceContext`:
 * the admin app provides the per-browser setting, the other apps keep MJPEG.
 */

import { createContext, useEffect, useState, useSyncExternalStore } from 'react';
import type { GetVideoAuth, VideoAuth } from '@verne/robot-video';

import { robotId } from './config';
import { HUB_URL } from './constants';

export type CameraSource = 'sfu' | 'mjpeg';

export const LocalCameraSourceContext = createContext<CameraSource>('mjpeg');

async function readAuth(res: Response, what: string): Promise<VideoAuth> {
  if (!res.ok) throw new Error(`${what} HTTP ${res.status}`);
  return (await res.json()) as VideoAuth;
}

/** Token source for the SFU. `token` is the Cognito id token for the hub. */
export function videoAuth(token: string | null | undefined): GetVideoAuth {
  if (robotId) {
    return async (id) =>
      readAuth(
        await fetch(`${HUB_URL}/video/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ robot_id: id, role: 'view' }),
        }),
        'video token',
      );
  }
  return async () =>
    readAuth(
      await fetch('/data/camera/video_token', {
        cache: 'no-store',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }),
      'video token',
    );
}

let localRobotIdPromise: Promise<string> | null = null;

function fetchLocalRobotId(): Promise<string> {
  if (!localRobotIdPromise) {
    localRobotIdPromise = fetch('/kiosk/info', { cache: 'no-store' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`/kiosk/info HTTP ${res.status}`);
        return ((await res.json()) as { robot_id: string }).robot_id;
      })
      .catch((e: unknown) => {
        localRobotIdPromise = null;
        throw e;
      });
  }
  return localRobotIdPromise;
}

/** The robot to pull video from: `?robot_id=` in hub mode, `/kiosk/info` in local mode. */
export function useVideoRobotId(): { id: string | null; error: string | null } {
  const [local, setLocal] = useState<{ id: string | null; error: string | null }>({ id: null, error: null });
  useEffect(() => {
    if (robotId) return;
    let alive = true;
    fetchLocalRobotId().then(
      (id) => alive && setLocal({ id, error: null }),
      (e: unknown) => alive && setLocal({ id: null, error: e instanceof Error ? e.message : String(e) }),
    );
    return () => {
      alive = false;
    };
  }, []);
  return robotId ? { id: robotId, error: null } : local;
}

const SOURCE_KEY = 'verne.dashboard.cameraSource';
const sourceListeners = new Set<() => void>();

function readCameraSource(): CameraSource {
  try {
    return localStorage.getItem(SOURCE_KEY) === 'mjpeg' ? 'mjpeg' : 'sfu';
  } catch {
    return 'sfu';
  }
}

let cameraSource: CameraSource = readCameraSource();

export function setCameraSource(source: CameraSource): void {
  cameraSource = source;
  try {
    localStorage.setItem(SOURCE_KEY, source);
  } catch (e) {
    console.warn('camera source not saved; it applies to this page only', e);
  }
  sourceListeners.forEach((l) => l());
}

/** The admin per-browser "Camera source" setting. SFU when storage is unavailable. */
export function useCameraSourceSetting(): CameraSource {
  return useSyncExternalStore(
    (l) => {
      sourceListeners.add(l);
      return () => sourceListeners.delete(l);
    },
    () => cameraSource,
  );
}
